import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Percent, ShieldCheck } from "lucide-react";

const fmt = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function ExtratoTaxa() {
  const [d, setD] = useState(null);
  const [cobrancas, setCobrancas] = useState([]);
  useEffect(() => {
    api.get("/montadora/extrato-taxa").then(r => setD(r.data)).catch(e => toast.error(formatApiError(e)));
    api.get("/montadora/cobrancas-taxa").then(r => setCobrancas(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="p-8 text-sm text-muted-foreground">Carregando demonstrativo…</div>;
  return (
    <div className="p-8" data-testid="extrato-taxa-page">
      <div className="label-cap"><Percent size={13} className="inline mr-1" />Financeiro · Taxa de Licenciamento (Cláusula 21)</div>
      <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Extrato da Taxa MontaJá</h1>
      <p className="text-xs text-muted-foreground mt-1">
        5% sobre valores efetivamente recebidos, após a carência de 6 meses. Valores ainda não pagos pela Empresa não entram na base.
      </p>

      {d.em_carencia && (
        <div className="mt-4 border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm flex items-center gap-2" data-testid="carencia-banner">
          <ShieldCheck size={16} className="text-emerald-600" />
          <span><b>Você está no período de carência</b> — nenhuma taxa é cobrada até {new Date(d.carencia_fim).toLocaleDateString("pt-BR")}.</span>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <div className="border border-border bg-white p-4">
          <div className="label-cap">Total recebido (base)</div>
          <div className="font-heading font-extrabold text-2xl mt-1" data-testid="taxa-total-base">{fmt(d.total_recebido)}</div>
        </div>
        <div className="border border-border bg-white p-4">
          <div className="label-cap">Isento (carência)</div>
          <div className="font-heading font-extrabold text-2xl mt-1 text-emerald-600" data-testid="taxa-total-isento">{fmt(d.total_isento_carencia)}</div>
        </div>
        <div className="border-2 border-[#142766] bg-white p-4">
          <div className="label-cap">Taxa devida (5%)</div>
          <div className="font-heading font-extrabold text-2xl mt-1 text-[#142766]" data-testid="taxa-total-devida">{fmt(d.total_taxa_devida)}</div>
        </div>
      </div>

      {cobrancas.length > 0 && (
        <div className="mt-6 border-2 border-[#FF6700] bg-white" data-testid="cobrancas-taxa">
          <div className="p-3 label-cap border-b border-border">Cobranças emitidas (Asaas)</div>
          <table className="w-full text-xs">
            <tbody>
              {cobrancas.map(c => (
                <tr key={c.id} className="border-b border-dashed border-border" data-testid={`cobranca-${c.competencia}`}>
                  <td className="p-2.5 font-bold">Competência {c.competencia}</td>
                  <td className="p-2.5">{fmt(c.valor)}</td>
                  <td className="p-2.5">Venc.: {new Date(c.vencimento + "T12:00:00").toLocaleDateString("pt-BR")}</td>
                  <td className="p-2.5">
                    {c.status === "paga"
                      ? <span className="text-emerald-600 font-bold">PAGA ✓</span>
                      : <a href={c.invoice_url} target="_blank" rel="noreferrer"
                          className="text-[#FF6700] font-bold underline"
                          data-testid={`pagar-cobranca-${c.competencia}`}>Pagar (Pix/boleto/cartão) →</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 border border-border bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-left">
              {["Atendimento", "Empresa", "Tipo", "Remuneração", "Recebido?", "Carência", "%", "Taxa"].map(h => (
                <th key={h} className="p-2.5 label-cap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.itens.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">Nenhum atendimento financeiro registrado ainda.</td></tr>
            )}
            {d.itens.map((i, k) => (
              <tr key={k} className="border-b border-border/50" data-testid={`taxa-item-${i.pim_code || k}`}>
                <td className="p-2.5 font-bold">{i.pim_code || "—"}</td>
                <td className="p-2.5">{i.empresa}</td>
                <td className="p-2.5 capitalize">{i.tipo || "—"}</td>
                <td className="p-2.5">{fmt(i.remuneracao_montadora)}</td>
                <td className="p-2.5">{i.recebido ? <span className="text-emerald-600 font-bold">Sim</span> : <span className="text-amber-600">Pendente</span>}</td>
                <td className="p-2.5">{i.isento_carencia ? "Isento" : "—"}</td>
                <td className="p-2.5">{i.percentual}%</td>
                <td className="p-2.5 font-bold">{i.taxa > 0 ? fmt(i.taxa) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-muted-foreground mt-3">
        Demonstrativo conforme Cláusula 21 do Contrato Mestre: carência de 6 meses a partir da ativação; base composta
        exclusivamente por valores efetivamente recebidos; cancelamentos/estornos são excluídos da apuração.
      </p>
    </div>
  );
}
